import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getActiveHoliday } from '../lib/holiday';
import { withTimeout } from '../lib/withTimeout';
import ClosedScreen from '../components/ClosedScreen';
import StoreNav from '../components/StoreNav';
import { useCart } from '../context/CartContext';

function VariantRow({ variant, selected, onSelect }) {
  return (
    <button onClick={onSelect} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
      padding: '13px 16px', borderRadius: 12, textAlign: 'left',
      border: selected ? '2px solid var(--primary)' : '2px solid var(--line)',
      background: selected ? 'var(--primary-soft)' : 'var(--paper)',
      transition: 'border-color .15s, background .15s',
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--ink)' }}>{variant.name}</div>
        <div style={{ fontSize: '.74rem', color: 'var(--ink-soft)', marginTop: 1 }}>{variant.isi}</div>
      </div>
      <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.05rem', color: 'var(--primary)' }}>
        Rp {(variant.price || 0).toLocaleString('id-ID')}
      </div>
    </button>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [holiday, setHoliday] = useState(undefined);

  useEffect(() => { load(); }, [id]);

  async function load() {
    const activeHoliday = await withTimeout(getActiveHoliday(), 6000, null);
    setHoliday(activeHoliday || null);
    if (activeHoliday) return;

    const { data: p } = await supabase.from('products').select('*').eq('id', id).single();
    setProduct(p);
    if (p) {
      const { data: vs } = await supabase.from('product_variants').select('*').eq('product_id', p.id).order('sort_order');
      setVariants(vs || []);
      if (vs && vs.length > 0) setSelectedVariant(vs[0]);
    }
  }

  if (holiday === undefined) {
    return (
      <div className="store" style={{ minHeight: '100vh' }}>
        <StoreNav />
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-soft)' }}>Memuat...</div>
      </div>
    );
  }

  if (holiday) {
    return <ClosedScreen holiday={holiday} />;
  }

  if (!product) {
    return (
      <div className="store" style={{ minHeight: '100vh' }}>
        <StoreNav />
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-soft)' }}>Memuat...</div>
      </div>
    );
  }

  const hasVariants = variants.length > 0;
  const unitPrice = hasVariants ? (selectedVariant?.price || 0) : (product.price || 0);
  const subtotal = unitPrice * qty;

  function handleAdd() {
    addItem({
      product_id: product.id,
      product_name: product.name,
      variant_id: hasVariants ? selectedVariant.id : null,
      variant_name: hasVariants ? selectedVariant.name : '',
      isi: hasVariants ? selectedVariant.isi : product.isi,
      price: unitPrice,
      image_url: product.image_url,
    }, qty);
    setAdded(true);
    setTimeout(() => nav('/cart'), 500);
  }

  return (
    <div className="store" style={{ minHeight: '100vh', paddingBottom: 100 }}>
      <StoreNav />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px 40px' }}>
        <button onClick={() => nav(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: 'var(--ink-soft)', fontSize: '.82rem', marginBottom: 20, padding: 0, cursor: 'pointer' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Kembali
        </button>

        <div className="pd-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>
          <div style={{ width: '100%', aspectRatio: '4/4', borderRadius: 20, overflow: 'hidden', background: product.image_url ? undefined : 'linear-gradient(150deg,#EFE7DA,#F7F2EA)' }}>
            {product.image_url
              ? <img src={product.image_url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.4rem', color: 'var(--ink-soft)' }}>{product.name}</div>}
          </div>

          <div>
            <h1 style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '2rem', color: 'var(--ink)', marginBottom: 10 }}>{product.name}</h1>
            {product.description && <p style={{ fontSize: '.9rem', color: 'var(--ink-soft)', lineHeight: 1.7, marginBottom: 22 }}>{product.description}</p>}

            {hasVariants ? (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: '.74rem', color: 'var(--ink-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Pilih Ukuran</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {variants.map(v => (
                    <VariantRow key={v.id} variant={v} selected={selectedVariant?.id === v.id} onSelect={() => setSelectedVariant(v)} />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.3rem', color: 'var(--primary)', marginBottom: 22 }}>
                Rp {unitPrice.toLocaleString('id-ID')} <span style={{ fontSize: '.78rem', color: 'var(--ink-soft)', fontFamily: "'DM Sans',sans-serif" }}>· {product.isi}</span>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <span style={{ fontSize: '.78rem', color: 'var(--ink-soft)', fontWeight: 600 }}>Jumlah</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1.5px solid var(--line)', borderRadius: 999, padding: '4px 6px' }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--sand)', border: 'none', fontSize: '1rem' }}>−</button>
                <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 600 }}>{qty}</span>
                <button onClick={() => setQty(q => q + 1)} style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--sand)', border: 'none', fontSize: '1rem' }}>+</button>
              </div>
            </div>

            <button onClick={handleAdd} disabled={added} style={{
              width: '100%', padding: '15px', borderRadius: 999, border: 'none',
              background: added ? '#2E8B57' : 'var(--primary)', color: '#fff', fontSize: '.94rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {added ? 'Ditambahkan ✓' : `Tambah ke Keranjang · Rp ${subtotal.toLocaleString('id-ID')}`}
            </button>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 700px) { .pd-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}
